import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import slugify from '../../../../../core/commom/slugify';
import { useUrlSearchParams } from '../../../../../core/hooks/useCustomSearchParams';
import { useGetContentListByDay } from '../../../hooks/DailySchedule/useGetContentListByDay';
import { getWeekSchedule } from '../../../queries/WeekSchedule';
import { getWeekSchedule as getHubWeekSchedule } from '../../../queries/ScheduleHub';
import { useDailyScheduleContentStore } from '../../../stores/useDailyScheduleContent';
import FooterMenu from '../Footermenu';
import ResponsiveHeader from '../ResponsiveHeader';
import { TemplateHeader } from '../TemplateHeader';
import { PAMainCard } from '../../PlanConfig/MainCard';
import BrainLoadingWrapper from '../../../../../core/components/molecules/BrainLoading';
import type { IDropDownItems } from '../../../../../core/components/molecules/DropDownList';
import { IWeekSchedule } from '../../../model/weekSchedule';
import { ScheduleDayEntry } from '../../../model/ScheduleHub';
import { capitalizeFirstLetter } from '../../../utils/common';
import { format, addHours, startOfWeek, endOfWeek } from 'date-fns';
import { getDayName } from '../../../../../core/utils/dataTools';

interface IDayScheduleTemplate {
  children: React.ReactNode;
}

/**
 * Quando a API /semana/{week} retorna dias/days vazios (cronograma futuro),
 * converte os dados do hub para o formato IWeekSchedule.
 */
function enrichWeekScheduleFromHub(
  base: IWeekSchedule,
  hubDays: ScheduleDayEntry[]
): IWeekSchedule {
  const days = hubDays.map((entry) => {
    // Usa getDayName pra obter o nome curto (segunda, terca, etc.)
    const shortName = getDayName(entry.dayKey);
    return {
      id: entry.dayKey,
      title: shortName,
      subtitle: format(addHours(new Date(entry.date), 3), 'dd/MM/yyyy'),
    };
  });

  const dias: Record<string, Array<{ nome: string; title: string; percentualConclusao: number }>> = {};

  for (const entry of hubDays) {
    // Usa getDayName pra gerar a key correta: "segunda", "terca", etc.
    const key = getDayName(entry.dayKey);
    const disciplines = entry.contents.map((c) => ({
      nome: c.discipline,
      title: c.discipline,
      percentualConclusao: c.progress,
    }));
    const unique = disciplines.filter(
      (d, i, arr) => arr.findIndex((x) => x.nome === d.nome) === i
    );
    dias[key] = unique;
  }

  return {
    ...base,
    days: days.length > 0 ? days : base.days,
    dias: Object.keys(dias).length > 0 ? dias as any : base.dias,
  };
}

export const DayScheduleTemplate: React.FC<IDayScheduleTemplate> = ({
  children,
}) => {
  const { scheduleId } = useParams();
  const [searchParamsFromRouter] = useSearchParams();
  const searchParams = useUrlSearchParams();

  const { mutate: mutateContentListByDay, isPending } =
    useGetContentListByDay();

  const [weekSchedule, setWeekSchedule] = useState<IWeekSchedule | null>(null);

  const [requestParams, setRequestParams] = useState({
    scheduleId: scheduleId || '',
    module: '',
    day: '',
  });

  const {
    setSelectedDiscipline,
    setSelectedDay,
    setSelectedContent,
    setContentList,
    setIsLoadingContentList,
    selectedDiscipline,
    selectedDay,
    setSelectedWeek,
    setContentListFilter,
  } = useDailyScheduleContentStore((s) => ({
    setSelectedDay: s.setSelectedDay,
    setSelectedDiscipline: s.setSelectedDiscipline,
    setSelectedContent: s.setSelectedContent,
    setContentList: s.setContentList,
    setIsLoadingContentList: s.setIsLoadingContentList,
    selectedDiscipline: s.selectedDiscipline,
    selectedDay: s.selectedDay,
    setSelectedWeek: s.setSelectedWeek,
    setContentListFilter: s.setContentListFilter,
  }));

  // 1. Fetch week schedule, fallback to hub API if dias/days are empty
  useEffect(() => {
    if (!scheduleId) return;

    const week = searchParamsFromRouter.get('semana') || '1';
    const weekNumber = parseInt(week) || 1;

    setSelectedWeek(weekNumber);

    const dateParam = searchParamsFromRouter.get('data');

    getWeekSchedule(scheduleId, weekNumber)
      .then(async (data) => {
        const hasDias = data.dias && Object.keys(data.dias).some(
          (key) => Array.isArray((data.dias as any)[key]) && (data.dias as any)[key].length > 0
        );
        const hasDays = Array.isArray(data.days) && data.days.length > 0;

        if (!hasDias || !hasDays) {
          try {
            // Usa a data real do conteúdo (param ?data=) pra calcular a semana correta
            const refDate = dateParam ? new Date(dateParam + 'T12:00:00') : new Date(data.dataInicio);
            const weekStart = startOfWeek(refDate, { weekStartsOn: 1 });
            const weekEnd = endOfWeek(refDate, { weekStartsOn: 1 });

            const hubData = await getHubWeekSchedule(scheduleId, weekStart, weekEnd);
            setWeekSchedule(enrichWeekScheduleFromHub(data, hubData.weekSchedule));
          } catch {
            setWeekSchedule(data);
          }
        } else {
          setWeekSchedule(data);
        }
      })
      .catch(() => {});
  }, [scheduleId, searchParamsFromRouter.get('semana')]);

  // 2. Process week schedule + URL params → set selected day/discipline + request params
  useEffect(() => {
    if (!weekSchedule) return;

    const weekDay = searchParamsFromRouter.get('dia') || 'segunda';
    const dateParam = searchParamsFromRouter.get('data');
    const day = weekSchedule.days?.find((item) => slugify(item.title) === weekDay);
    const isoDate = dateParam || day?.subtitle?.split('/').reverse().join('-') || '';

    const disciplineParam = searchParamsFromRouter.get('materia');
    let discipline = '';

    if (disciplineParam) {
      discipline = disciplineParam;
    } else {
      discipline = weekSchedule.dias?.[slugify(weekDay) as keyof typeof weekSchedule.dias]?.[0]?.nome || '';
    }

    setSelectedDay({
      id: day?.id || '',
      title: day?.title || weekDay,
      subtitle: day?.subtitle || '',
    });

    setSelectedDiscipline(discipline);

    setRequestParams({
      scheduleId: scheduleId || '',
      module: discipline || '',
      day: isoDate,
    });
  }, [weekSchedule, searchParamsFromRouter.toString()]);

  const handleUserSelectDay = useCallback(
    (day: IDropDownItems) => {
      setSelectedDay(day);
      setSelectedContent(null);
      if (!scheduleId || !weekSchedule) return;

      const daySlug = slugify(day.title) as keyof typeof weekSchedule.dias;
      const hasDiscipline = weekSchedule.dias?.[daySlug]?.some(
        (item) => item.nome === selectedDiscipline
      );
      let disciplineSlug = selectedDiscipline;
      if (!hasDiscipline) {
        disciplineSlug = weekSchedule.dias?.[daySlug]?.[0]?.nome || '';
        setSelectedDiscipline(disciplineSlug);
      }
      const isoDate = day.subtitle?.split('/').reverse().join('-') || '';
      searchParams.setUrlParam('dia', slugify(day.title));
      searchParams.setUrlParam('materia', disciplineSlug);
      setRequestParams({ scheduleId, module: disciplineSlug, day: isoDate });
    },
    [
      scheduleId,
      weekSchedule,
      selectedDiscipline,
      setSelectedDay,
      setSelectedDiscipline,
      setSelectedContent,
      searchParams,
    ]
  );

  const handleUserSelectDiscipline = useCallback(
    (discipline: string) => {
      setContentListFilter('all');
      setSelectedDiscipline(discipline);
      setSelectedContent(null);
      if (!selectedDay || !scheduleId) return;
      const isoDate =
        selectedDay.subtitle?.split('/').reverse().join('-') || '';
      searchParams.setUrlParam('materia', discipline);
      setRequestParams({ scheduleId, module: discipline, day: isoDate });
    },
    [
      scheduleId,
      selectedDay,
      setSelectedDiscipline,
      setSelectedContent,
      searchParams,
    ]
  );

  // 3. Fetch day contents
  useEffect(() => {
    if (
      !requestParams.day ||
      !requestParams.module ||
      !requestParams.scheduleId
    ) {
      return;
    }

    mutateContentListByDay(requestParams, {
      onSuccess: (data) => {
        setContentList(data || []);
      },
      onError: () => {
        setContentList([]);
      },
    });
  }, [requestParams]);

  useEffect(() => {
    setIsLoadingContentList(isPending);
  }, [isPending]);

  const normalize = (text: string) =>
    text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const filteredWeekSchedule = useMemo<IWeekSchedule>(() => {
    if (!weekSchedule) return {} as IWeekSchedule;

    const allDays = weekSchedule.days || [];

    const filteredDays = weekSchedule.dias
      ? allDays.filter((day) => {
          const key = normalize(day.title);
          const disciplinas =
            weekSchedule.dias[key as keyof typeof weekSchedule.dias];
          return Array.isArray(disciplinas) && disciplinas.length > 0;
        })
      : [];

    const daysToUse = filteredDays.length > 0 ? filteredDays : allDays;

    return {
      ...weekSchedule,
      days: daysToUse.map((day) => ({
        ...day,
        title: capitalizeFirstLetter(day?.title || ''),
      })),
    };
  }, [weekSchedule]);

  if (!weekSchedule) {
    return <BrainLoadingWrapper />;
  }

  return (
    <PAMainCard>
      <div className='flex flex-col gap-8'>
        <TemplateHeader
          weekSchedule={filteredWeekSchedule}
          onDaySelect={handleUserSelectDay}
          onDisciplineSelect={handleUserSelectDiscipline}
        />

        <ResponsiveHeader
          weekSchedule={filteredWeekSchedule}
          onDaySelect={handleUserSelectDay}
          onDisciplineSelect={handleUserSelectDiscipline}
        />

        <div className='grow overflow-hidden'>
          {isPending ? <BrainLoadingWrapper /> : children}
        </div>

        <FooterMenu />
      </div>
    </PAMainCard>
  );
};
